import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Button } from './Button.js';
import { Sheet } from './Sheet.js';
import { useI18n } from '../i18n/index.js';
import {
  clampPhoto,
  fitScale,
  initialCrop,
  initialPhoto,
  outputSize,
  resizeCrop,
  toSourceRect,
  type Corner,
  type Rect,
} from '../lib/cropGeometry.js';
import { ImageFileError, MAX_OUTPUT_EDGE, renderCrop } from '../lib/imageFile.js';
import styles from './ImageCropper.module.css';

interface ImageCropperProps {
  /** 방금 고른 사진. `null`이면 닫힌다 */
  bitmap: ImageBitmap | null;
  /** 잘라 낼 비율. 격자 칸은 4/5, 프로필 사진은 1 */
  aspect: number;
  /** 동그란 자리에 붙을 사진은 틀도 동그랗게 — 붙는 자리와 같아야 한다 */
  round?: boolean;
  onCancel: () => void;
  onDone: (blob: Blob) => void;
  onError: (error: unknown) => void;
}

const CORNERS: Corner[] = ['nw', 'ne', 'sw', 'se'];

/**
 * 사진 자르기.
 *
 * **왜 필요한가**: 격자 칸은 4:5인데 올라오는 사진은 제각각이다. 그냥 넣으면
 * 브라우저가 가운데를 기준으로 잘라내는데, 보여주고 싶은 것(얼굴·물건)이
 * 가운데 있으리란 법이 없다. 어디를 남길지는 올리는 사람이 정해야 한다.
 *
 * **어떻게 두는가**: 틀은 판 위에 붙박이고, 그 밑에서 **사진을 끌어 옮긴다.**
 * 남길 자리는 늘 같은 데 있고 사진이 그 아래를 지나간다 — 휴대폰 사진 앱과
 * 프로필 사진 등록에서 다들 쓰는 손놀림이라 따로 배울 것이 없다. 틀 크기는
 * 모서리를 잡아 바꾼다.
 *
 * 처음에 사진을 통째로 보여주는 것이 중요하다 — 잘려 보이는 상태에서는 화면
 * 밖에 무엇이 있는지 모르는 채로 자리를 골라야 한다.
 *
 * **비율은 잠겨 있다.** 자유롭게 늘리면 결과가 칸에 안 맞아 한 번 더 잘리고,
 * 그러면 사용자가 고른 자리가 또 흔들린다.
 */
export function ImageCropper({
  bitmap,
  aspect,
  round = false,
  onCancel,
  onDone,
  onError,
}: ImageCropperProps) {
  const { t } = useI18n();
  const stageRef = useRef<HTMLDivElement>(null);
  /** 판 위에 놓인 사진의 자리와 크기. 끌면 이것이 움직인다 */
  const [photo, setPhoto] = useState<Rect | null>(null);
  /** 남길 자리. 판에 붙박이라 사진을 끌어도 그대로다 */
  const [crop, setCrop] = useState<Rect | null>(null);
  const [busy, setBusy] = useState(false);
  const url = useBitmapUrl(bitmap);

  /** 끌기 시작 시점의 상태. 손을 떼면 비운다 */
  const drag = useRef<{
    corner: Corner | null;
    x: number;
    y: number;
    crop: Rect;
    photo: Rect;
  } | null>(null);

  /*
   * 판의 크기는 **그려진 뒤에** 잰다. 시트가 열리는 중에는 0이라 그 값으로
   * 틀을 잡으면 아무것도 안 보인다. 화면이 바뀌면 다시 잰다.
   *
   * 사진과 틀을 **함께** 놓는다 — 틀은 사진 안에 있어야 하므로 둘을 따로
   * 정하면 한쪽만 갱신된 찰나에 틀이 사진 밖으로 나간다.
   */
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (stage === null || bitmap === null) {
      return;
    }
    const place = (): void => {
      const box = stage.getBoundingClientRect();
      if (box.width === 0 || box.height === 0) {
        return;
      }
      const scale = fitScale(box, bitmap);
      const next = initialPhoto(box, {
        width: bitmap.width * scale,
        height: bitmap.height * scale,
      });
      setPhoto(next);
      setCrop(initialCrop(next, aspect));
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [bitmap, aspect]);

  if (bitmap === null) {
    return null;
  }

  const pointIn = (event: React.PointerEvent): { x: number; y: number } => {
    const box = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - box.left, y: event.clientY - box.top };
  };

  const startDrag = (corner: Corner | null) => (event: React.PointerEvent<HTMLElement>) => {
    if (crop === null || photo === null) {
      return;
    }
    // 손잡이를 잡았으면 사진 끌기로 내려가지 않게 막는다
    event.stopPropagation();
    // 좌표는 언제나 **판 기준**이다 — 잡은 것이 손잡이여도 마찬가지다
    const box = stageRef.current?.getBoundingClientRect();
    if (box === undefined) {
      return;
    }
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    drag.current = {
      corner,
      x: event.clientX - box.left,
      y: event.clientY - box.top,
      crop,
      photo,
    };
  };

  const onMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const start = drag.current;
    if (start === null) {
      return;
    }
    const point = pointIn(event);
    if (start.corner === null) {
      // 사진을 끌었다 — 틀은 그 자리에 두고 사진만 미끄러진다
      setPhoto(
        clampPhoto(
          {
            ...start.photo,
            x: start.photo.x + (point.x - start.x),
            y: start.photo.y + (point.y - start.y),
          },
          start.crop,
        ),
      );
      return;
    }
    setCrop(resizeCrop(start.crop, start.corner, point, start.photo, aspect));
  };

  const endDrag = (): void => {
    drag.current = null;
  };

  const confirm = async (): Promise<void> => {
    if (crop === null || photo === null) {
      return;
    }
    setBusy(true);
    try {
      const scale = photo.width / bitmap.width;
      const source = toSourceRect(crop, photo, scale, bitmap);
      const blob = await renderCrop(bitmap, source, outputSize(aspect, source, MAX_OUTPUT_EDGE));
      onDone(blob);
    } catch (cause) {
      onError(cause instanceof ImageFileError ? cause : new ImageFileError('decode'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open onClose={onCancel} title={t('image.cropTitle')}>
      {/*
        ⚠️ 끌기를 **판**이 받는다. 사진 위만 받으면, 사진을 끝까지 밀어
        가장자리가 판 안으로 들어온 순간부터 빈 자리를 잡게 되고 그때만
        아무 일도 안 일어난다.

        모서리 손잡이는 여기서 이벤트를 가로채(`stopPropagation`) 크기
        바꾸기로 간다.
      */}
      <div
        ref={stageRef}
        className={styles.stage}
        onPointerDown={startDrag(null)}
        onPointerMove={onMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {photo === null ? null : (
          <div
            className={styles.photo}
            style={{ left: photo.x, top: photo.y, width: photo.width, height: photo.height }}
          >
            <img className={styles.image} src={url} alt="" draggable={false} />
          </div>
        )}

        {/*
          틀은 **사진 바깥**에 둔다 — 사진 안에 두면 사진을 끌 때 같이
          따라가 버려서, 움직이는 것이 무엇인지 눈에 안 보인다.
        */}
        {crop === null ? null : (
          <>
            {/*
              틀 **바깥**을 어둡게 덮는다. 테두리만 그리면 밝은 사진에서
              어디까지가 남는 부분인지 눈으로 안 잡힌다.
            */}
            <div
              className={styles.shade}
              style={{
                clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${px(crop.x)} ${px(crop.y)}, ${px(crop.x)} ${px(crop.y + crop.height)}, ${px(crop.x + crop.width)} ${px(crop.y + crop.height)}, ${px(crop.x + crop.width)} ${px(crop.y)}, ${px(crop.x)} ${px(crop.y)})`,
              }}
            />
            <div
              className={styles.crop}
              data-round={round || undefined}
              style={{ left: crop.x, top: crop.y, width: crop.width, height: crop.height }}
            >
              {CORNERS.map((corner) => (
                <span
                  key={corner}
                  className={styles.handle}
                  data-corner={corner}
                  onPointerDown={startDrag(corner)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className={styles.actions}>
        <Button variant="outline" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <Button loading={busy} onClick={() => void confirm()}>
          {t('common.confirm')}
        </Button>
      </div>
    </Sheet>
  );
}

const px = (value: number): string => `${value}px`;

/**
 * 비트맵을 `<img>`에 붙일 주소로.
 *
 * 캔버스로 직접 그리지 않는 이유: 틀을 끌 때마다 다시 그리면 큰 사진에서
 * 버벅인다. `<img>`는 한 번 그려 두면 틀만 움직인다.
 */
function useBitmapUrl(bitmap: ImageBitmap | null): string {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (bitmap === null) {
      setUrl('');
      return;
    }
    let revoked = false;
    let created = '';
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
    canvas.toBlob((blob) => {
      if (blob === null || revoked) {
        return;
      }
      created = URL.createObjectURL(blob);
      setUrl(created);
    });
    return () => {
      revoked = true;
      if (created !== '') {
        URL.revokeObjectURL(created);
      }
    };
  }, [bitmap]);
  return url;
}
